import {
    isRenderable,
    isSlippable,
    slip,
    type RenderItem,
    type Renderable,
} from "@circleofa/acim-engine"
import { acim } from "./acim"
import { writable } from "svelte/store"

class InvalidRedirectionPathError extends Error {
    #store = writable("")
    readonly subscribe = this.#store.subscribe
    constructor(public path: string) {
        const message = `Invalid redirection tab path: ${path}`
        super(message)
        this.name = "InvalidRedirectionPathError"
    }
}

/**
 * Converts a path like T-2.I:1-2 or [T-1:1]-[T-1.1] to the appropriate
 * Renderable#Path format
 * @param path
 * @returns
 * @throws an error if redirection is wrong
 */
export function getRedirectionPath(path: string): string {
    const found = acim.find(path)
    if (found && isSlippable(found)) {
        if (isRenderable(found)) {
            return path
        } else {
            if (found.slipDirection == "up") {
                return slip(found).path + "#" + path
            } else {
                return slip(found).path
            }
        }
    } else {
        const range = acim.fromRange(path)
        if (!range) throw new InvalidRedirectionPathError(path)
        const renderable = slip(range[0])
        return renderable.path + "#" + path
    }
}

/**
 * Checks to see if the range can actually be converted
 * into an URL successfully.
 * @param {string} path incoming path like T-3.II:2-3
 * @returns {boolean} if it can be transformed into an URL
 */
export function isRangePathURLable(path: string): boolean {
    try {
        convertRangePathToURLPath(path)
        return true
    } catch {
        return false
    }
}

/**
 * Transforms a path from a url, that may or may not be a range, into
 * something that can be understood by the /book/[path] logic.
 *
 * Add "book" manually
 *
 * @param {string} path
 * @returns {string}
 * @throws if find does something wrong or fromRange doesn't work
 */
export function convertRangePathToURLPath(path: string): string {
    const found = acim.find(path)
    if (found) {
        if (isRenderable(found)) {
            return path
        } else {
            if (isSlippable(found)) {
                if (found.slipDirection == "up") {
                    return slip(found).path + "#" + path
                } else {
                    return slip(found).path
                }
            } else {
                throw new Error("Neither slippable nor renderable! " + path)
            }
        }
    } else {
        const range = acim.fromRange(path)
        if (!range) throw new Error("Not a range: " + path)
        const renderable = slip(range[0])
        return renderable.path + "#" + path
    }
}

// @ts-ignore
globalThis.fromRangex = (x: string) => acim.fromRange(x)

/**
 * This replicates what happens via a cloudflare worker
 * when one is sent from copy-pasted data.
 *
 * It's important to hook this into tabs, otherwise if the app is open
 * it will overwrite the currently selected tab.
 *
 * @param {string} path
 * @returns {string | null} path to redirect to, or null if there's none
 */
export function testRedirectionPath(path = window.location.pathname): null | string {
    try {
        const match = /^\/:(.+)$/.exec(path)
        if (!match) return null
        const rest = match[1]
        if (match) {
            const rest = match[1]
            return "/book/" + convertRangePathToURLPath(rest)
        }
    } catch (e) {
        console.warn(e)
        return null
    }
    return null
}
